import { z } from 'zod'

export const VOE_RESPONSE_VALIDATION_SUMMARY =
  'Please complete the required fields before submitting.'

const isBlank = (value?: string | null) => !value?.trim()

type AccidentLike = {
  city?: string | null
  state?: string | null
  description?: string | null
  accidentDate?: Date | string | null
}

export const isEmptyAccident = (accident?: AccidentLike | null) =>
  !accident ||
  (isBlank(accident.city) &&
    isBlank(accident.state) &&
    isBlank(accident.description) &&
    !accident.accidentDate)

const accidentRowSchema = z.object({
  accidentDate: z.any().optional().nullable(),
  city: z.string().min(1, { message: 'This field is required' }).max(100),
  state: z.string().min(2, { message: 'This field is required' }).max(50),
  description: z
    .string()
    .min(1, { message: 'This field is required' })
    .max(1000),
  isPreventable: z.boolean().optional().nullable(),
  isCommercialMotorVehicleInvolved: z.boolean().optional().nullable(),
  involvedFatalities: z.boolean().optional().nullable(),
  involvedInjuries: z.boolean().optional().nullable(),
})

const requiredBoolean = z.boolean({
  required_error: 'This field is required',
  invalid_type_error: 'This field is required',
})

export const voeResponseFormSchema = z
  .object({
    positionHeld: z
      .string({ required_error: 'This field is required' })
      .min(1, { message: 'This field is required' })
      .max(200, { message: '200 character max' }),
    startDate: z.date({
      required_error: 'This field is required',
      invalid_type_error: 'This field is required',
    }),
    endDate: z.date().optional().nullable(),
    reasonForLeaving: z
      .string()
      .max(1000, { message: '1000 character max' })
      .optional()
      .nullable(),
    trailerTypes: z.string().max(500).optional().nullable(),
    isDriverTerminated: requiredBoolean,
    isDriverSubjectToFMCSRs: requiredBoolean,
    isJobDesignatedAsSafetySensitiveFunctionUnderDOT: requiredBoolean,
    eligibleForRehire: requiredBoolean,
    accidents: z.array(z.any()).optional().nullable(),
  })
  .superRefine((data, ctx) => {
    ;(data.accidents ?? []).forEach((accident, index) => {
      if (isEmptyAccident(accident)) return

      const parsed = accidentRowSchema.safeParse(accident)
      if (parsed.success) return

      parsed.error.issues.forEach((issue) => {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: issue.message,
          path: ['accidents', index, ...issue.path],
        })
      })
    })
  })

export type VoeResponseFormValues = z.infer<typeof voeResponseFormSchema>

export const sanitizeVoeResponseAccidents = (
  accidents?: Array<Record<string, unknown>> | null
) =>
  (accidents ?? [])
    .filter((accident) => !isEmptyAccident(accident as AccidentLike))
    .map((accident) => {
      const { __typename, createdAt, updatedAt, ...rest } = accident
      return rest
    })

export const toVoeResponseSubmitInput = (
  values: z.infer<typeof voeResponseFormSchema>
) => ({
  ...values,
  reasonForLeaving: values.reasonForLeaving?.trim() || null,
  accidents: sanitizeVoeResponseAccidents(
    values.accidents as Array<Record<string, unknown>> | undefined
  ),
})

const accidentIssues = (
  accidents: unknown[] | null | undefined,
  ctx: z.RefinementCtx
) => {
  ;(accidents ?? []).forEach((accident, index) => {
    if (isEmptyAccident(accident as AccidentLike)) return

    const parsed = accidentRowSchema.safeParse(accident)
    if (parsed.success) return

    parsed.error.issues.forEach((issue) => {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: issue.message,
        path: ['accidents', index, ...issue.path],
      })
    })
  })
}

export const voeEmployerResponseFormSchema = z
  .object({
    wasEmployed: requiredBoolean,
    positionHeld: z
      .string()
      .max(200, { message: '200 character max' })
      .optional()
      .nullable(),
    startDate: z.date().optional().nullable(),
    endDate: z.date().optional().nullable(),
    reasonForLeaving: z
      .string()
      .max(1000, { message: '1000 character max' })
      .optional()
      .nullable(),
    trailerTypes: z.string().max(500).optional().nullable(),
    isDriverTerminated: z.boolean().optional().nullable(),
    isDriverSubjectToFMCSRs: z.boolean().optional().nullable(),
    isJobDesignatedAsSafetySensitiveFunctionUnderDOT: z
      .boolean()
      .optional()
      .nullable(),
    eligibleForRehire: z.boolean().optional().nullable(),
    accidents: z.array(z.any()).optional().nullable(),
  })
  .superRefine((data, ctx) => {
    if (data.wasEmployed !== true) return

    const required = (path: string, filled: boolean) => {
      if (filled) return
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'This field is required',
        path: [path],
      })
    }

    required('positionHeld', Boolean(data.positionHeld?.trim()))
    required('startDate', data.startDate instanceof Date)
    required('isDriverTerminated', typeof data.isDriverTerminated === 'boolean')
    required(
      'isDriverSubjectToFMCSRs',
      typeof data.isDriverSubjectToFMCSRs === 'boolean'
    )
    required(
      'isJobDesignatedAsSafetySensitiveFunctionUnderDOT',
      typeof data.isJobDesignatedAsSafetySensitiveFunctionUnderDOT === 'boolean'
    )
    required('eligibleForRehire', typeof data.eligibleForRehire === 'boolean')

    accidentIssues(data.accidents, ctx)
  })

export type VoeEmployerResponseFormValues = z.infer<
  typeof voeEmployerResponseFormSchema
>

export const toVoeEmployerResponseSubmitInput = (
  values: VoeEmployerResponseFormValues
) => {
  if (values.wasEmployed === false) {
    return {
      wasEmployed: false,
      positionHeld: null,
      startDate: null,
      endDate: null,
      reasonForLeaving: null,
      trailerTypes: null,
      isDriverTerminated: null,
      isDriverSubjectToFMCSRs: null,
      isJobDesignatedAsSafetySensitiveFunctionUnderDOT: null,
      eligibleForRehire: null,
      accidents: [],
    }
  }

  return {
    ...values,
    reasonForLeaving: values.reasonForLeaving?.trim() || null,
    accidents: sanitizeVoeResponseAccidents(
      values.accidents as Array<Record<string, unknown>> | undefined
    ),
  }
}
